import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { InjectedRouter, RouterState, PlainRoute } from 'react-router';
import { Location } from 'history';
import Row from 'components/Row';
import TutoringScheduleAvailabilitySelection, {
  WaitlistCopyAndLink,
} from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewSchedule/TutoringScheduleAvailabilitySelection';
import { CoachingReservation } from 'coaching/containers/AvailableCoachingSessionsContainer';
import CoachingConstants from 'coaching/CoachingConstants';
import ErrorReporter from 'app/components/ErrorReporter';
import { getNextNCoachingDays, getDatesFromReservations } from 'coaching/lib/timeIntervals';
import {
  getSelectedSubjectOrThrow,
  resolveChildName,
  getGradeLevelOrThrow,
  StoredTutoringReservation,
  saveReservationInfo,
  saveAvailabilityQuery,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';
import TimezoneHelper from 'lib/timezones';
import EventTracking from 'lib/EventTracking';
import { sendABTastyCustomEvent } from 'coaching/lib/abTasty';
import * as CoachingActions from 'coaching/actions/CoachingActions';
import {
  createTutoringReservation,
  CreateTutoringReservationResponse,
} from 'coaching/actions/CoachingActions';
import ToastActions from 'actions/ToastActions';
import Button from 'components/Button';
import Text from 'components/Text';
import { Moment } from 'moment-timezone/moment-timezone';
import { last } from 'lodash';
import { pushCustomEvent } from 'lib/googleTagManager';
import CoachingHeading from 'coaching/components/CoachingHeading';
import Copy from 'components/Copy';

const eventPrefix = 'tutoring_self_schedule_purchase.schedule';
const scheduleSessionEventPrefix = 'coaching.self_schedule_purchase.schedule_session';

export type TutoringSelectedReservation = {
  coachUuid: string;
  startDateTime: string;
  endDateTime: string;
};

export type TutoringSelfServeViewScheduleState = {
  selectedUserTimezone: string;
  reservationUuid?: string;
  currentDate: string;
  selectedReservation?: TutoringSelectedReservation;
};

export type TutoringSelfServeViewScheduleData = Omit<
  TutoringSelfServeViewScheduleState,
  'selectedReservation'
> & {
  selectedReservation: TutoringSelectedReservation;
};

export type ViewScheduleInputData = {
  selectedSubject: string;
  userTimezone: string;
  coachingAvailability?: CoachingActions.CoachingAvailabilityResponse | null;
};

export type Props = {
  router: InjectedRouter & RouterState;
  route: PlainRoute;
  goToNextState: () => void;

  restoredProps: {
    reservationInfo: StoredTutoringReservation | null;
    availabilityQuery: CoachingActions.CoachingAvailabilityResponse | null;
    selectedSubject: string;
  };
};

const toHydrateCoachAvailabilityResponse = (
  response: CoachingActions.CoachingAvailabilityResponse
) => {
  return {
    availableSessions: response?.availableSessions?.map(availableSession => ({
      coachUuid: availableSession?.coachUuid || '',
      startDateTime: moment(availableSession?.startDateTime),
      endDateTime: moment(availableSession?.endDateTime),
    })),
    timeOff: response?.timeOff?.map(timeOffInstance => ({
      startDate: moment(timeOffInstance?.startDate),
      endDate: moment(timeOffInstance?.endDate),
    })),
    error: response.error,
  };
};

export default function TutoringSelfServeViewSchedule(props: Props) {
  const { goToNextState } = props;
  const [coachingAvailabilityResponse, setCoachingAvailabilityResponse] = useState<
    CoachingActions.CoachingAvailabilityResponse | undefined
  >(props.restoredProps.availabilityQuery ?? undefined);
  const reservationInfo = props.restoredProps.reservationInfo;
  const [selectedReservation, setSelectedReservation] = useState(() => {
    return reservationInfo?.selectedReservation;
  });
  const [loading, setLoading] = useState({
    loadingAvailabilities: false,
    submittingReservation: false,
  });
  const [state, setState] = useState<TutoringSelfServeViewScheduleState>({
    selectedUserTimezone: TimezoneHelper.userTimezoneDefaultValue ?? 'America/Los_Angeles',
    reservationUuid: reservationInfo?.reservationUuid || '',
    currentDate: moment().toISOString(),
  });
  const selectedSubject = props.restoredProps.selectedSubject;
  useEffect(() => {
    sendABTastyCustomEvent('tutoring_schedule');
    pushCustomEvent('coaching_schedule_screen');
    EventTracking.trackKey(`${scheduleSessionEventPrefix}.view`);

    props.router.setRouteLeaveHook(props.route, (nextLocation: Location | undefined) => {
      if (nextLocation?.pathname === CoachingConstants.TUTORING_STUDENT_DETAILS) {
        EventTracking.trackKey(`${eventPrefix}.details.breadcrumb.click`, {
          student_name: resolveChildName() || '',
          tutoring_grade_level: getGradeLevelOrThrow(),
          tutoring_subject: getSelectedSubjectOrThrow(),
        });
      }
    });
  }, []);
  const dateTimeRange = getNextNCoachingDays(6, 0, state.selectedUserTimezone, state.currentDate);
  const startDateTimeRange = dateTimeRange[0]?.toISOString();
  const endDateTimeRange = last(dateTimeRange)?.toISOString();

  if (!startDateTimeRange || !endDateTimeRange) {
    return (
      <ErrorReporter error="TutoringSelfServeViewSchedule - tried to render schedule session without a datetime range">
        Oops! Looks like something went wrong. Please try refreshing the page.
      </ErrorReporter>
    );
  }
  const getAvailabilitySessions = async () => {
    if (!coachingAvailabilityResponse) {
      setLoading(prev => ({ ...prev, loadingAvailabilities: true }));
      const availabilityResponse = await CoachingActions.getAvailabilityCoachingSessions({
        subjects: [selectedSubject],
        startDateTimeRange,
        endDateTimeRange,
      });
      setLoading(prev => ({ ...prev, loadingAvailabilities: false }));
      setCoachingAvailabilityResponse(availabilityResponse);
    }
  };

  useEffect(() => {
    getAvailabilitySessions();
  }, [selectedSubject, startDateTimeRange, endDateTimeRange]);

  const { availableSessions: reservations, timeOff } = toHydrateCoachAvailabilityResponse(
    coachingAvailabilityResponse || {}
  );
  const daysToShow = getDatesFromReservations(
    reservations?.map(x => x.startDateTime) ?? dateTimeRange
  );

  const onContinueSelected = async () => {
    setLoading(prev => ({ ...prev, submittingReservation: true }));
    setState(previous => ({ ...previous }));
    const reservation = selectedReservation as CoachingReservation;
    if (reservationsAreTheSame(reservationInfo?.selectedReservation, selectedReservation)) {
      goToNextState();
      return;
    }
    const reservationMetadata = await createTutoringReservation({
      subject: selectedSubject,
      startDateTime: reservation.startDateTime.toISOString(),
      endDateTime: reservation.endDateTime.toISOString(),
      coachUuid: reservation.coachUuid,
    });

    setLoading(prev => ({ ...prev, submittingReservation: false }));
    setState(previous => ({ ...previous }));
    trackContinueClick(reservationMetadata, reservation, selectedSubject);
    // If it failed, don't advance to the purchase screen
    if (reservationMetadata.error) {
      ToastActions.showErrorToast(reservationMetadata.error);
      return;
    }
    if (reservationMetadata.reservationUuid && reservation) {
      saveReservationInfo({
        reservationUuid: reservationMetadata.reservationUuid,
        selectedReservation: reservation,
        selectedUserTimezone: state.selectedUserTimezone,
      });
      coachingAvailabilityResponse && saveAvailabilityQuery(coachingAvailabilityResponse);
      goToNextState();
    }
  };

  const disabledSetWeeklyButton = loading.submittingReservation || !selectedReservation;
  if (coachingAvailabilityResponse?.error) {
    return (
      <React.Fragment>
        <Row>
          <ErrorReporter
            error={`TutoringSelfServeViewScheduleSessionView - could not load reservations e=${coachingAvailabilityResponse?.error}`}>
            Oops, looks like something went wrong - please try refreshing the page.
          </ErrorReporter>
        </Row>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <Row data-test="schedule-session-header" size="large">
        <CoachingHeading data-test="schedule-session-heading">
          Schedule your first session
        </CoachingHeading>
        <Copy data-test="schedule-session-copy" align="left" size="small">
          This full-length session will be 40 minutes. Tutoring is available Monday through Sunday.
        </Copy>
      </Row>
      <Row>
        <TutoringScheduleAvailabilitySelection
          selectedSubject={selectedSubject}
          userTimezone={state.selectedUserTimezone}
          onSelectTimezone={tz => {
            setState({ ...state, selectedUserTimezone: tz });
          }}
          selectedReservation={selectedReservation}
          onSelectReservation={setSelectedReservation}
          daysToShow={daysToShow}
          reservations={reservations}
          timeOff={timeOff}
          loading={loading.loadingAvailabilities}
        />
        <WaitlistCopyAndLink
          flowVersion="streamlined"
          selectedSlotStart={selectedReservation?.startDateTime ?? null}
          selectedSlotEnd={selectedReservation?.endDateTime ?? null}
        />
        <Row size="small">
          <Button
            fullAtBreakpoint="480"
            data-test="schedule-session-continue-button"
            color="koolaid"
            onClick={onContinueSelected}
            loading={loading.submittingReservation}
            disabled={disabledSetWeeklyButton}>
            Select
          </Button>
        </Row>
        {selectedReservation && (
          <Text data-test="schedule-session-detail-note" size="xsmall" color="graphite">
            We’ll reserve every{' '}
            {formatReservationDate(selectedReservation.startDateTime, state.selectedUserTimezone)}{' '}
            for you with your tutor. Don’t worry, you can always adjust your schedule for future
            sessions.
          </Text>
        )}
      </Row>
    </React.Fragment>
  );
}

function formatReservationDate(reservationStartTime: Moment, timezone: string) {
  return moment(reservationStartTime.toISOString())
    .tz(timezone)
    .format('ddd[,] MMM DD [at] LT [(]z[)]');
}

function reservationsAreTheSame(
  reservationA: CoachingReservation | undefined,
  reservationB: CoachingReservation | undefined
) {
  if (!reservationA || !reservationB) return false;
  const coachesAreTheSame = reservationA.coachUuid === reservationB.coachUuid;
  const startDateTimeIsTheSame = moment(reservationA.startDateTime).isSame(
    reservationB.startDateTime
  );
  const endDateTimeIsTheSame = moment(reservationA.endDateTime).isSame(reservationB.endDateTime);
  return coachesAreTheSame && startDateTimeIsTheSame && endDateTimeIsTheSame;
}

function trackContinueClick(
  reservationMetadata: CreateTutoringReservationResponse,
  reservation: CoachingReservation,
  selectedSubject: string
) {
  EventTracking.trackKey(`${scheduleSessionEventPrefix}.continue.click`, {
    data: JSON.stringify({
      reservation_uuid: reservationMetadata.reservationUuid,
      coach_uuid: reservation.coachUuid,
      tutoring_subject: selectedSubject,
      start_date_time: reservation.startDateTime,
      end_date_time: reservation.endDateTime,
    }),
  });
}
